import axios from 'axios';
import { joinUrl } from 'lib/utils';
import { OAuthProvider, User } from '../types/auth';
import JWTRefreshClient from '../utils/jwtRefreshClient';
import { log, error } from '../utils/logger';
import { getAccessToken } from './tokenService';
import { jwtDecode } from 'jwt-decode';

interface OAuthResponse {
  code?: string;
  access_token?: string;
  refresh_token?: string;
}

interface TokenRefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  external_token_refreshed?: boolean;
}

interface TokenValidationResponse {
  valid: boolean;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organization: string;
    role: string;
  };
  should_refresh: boolean;
  expires_in: number;
}

class AuthService {
  private baseUrlV1 = joinUrl(process.env.REACT_APP_OAUTH_URL || process.env.REACT_APP_MONITOR_URL || 'https://monitor.openana.ai', 'api/v1/auth');
  private baseUrlV2 = joinUrl(process.env.REACT_APP_OAUTH_URL || process.env.REACT_APP_MONITOR_URL || 'https://monitor.openana.ai', 'api/v2/auth');
  private jwtClient: JWTRefreshClient | null = null;
  private silentRefreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.initializeJWTClient();
  }

  /**
   * Initialize JWT refresh client
   */
  private initializeJWTClient(): void {
    // This is no longer needed as the token refresh is handled by the axios interceptor
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpired(): void {
    this.logout();
    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<TokenRefreshResponse | null> {
    // This is no longer needed as the token refresh is handled by the axios interceptor
    return null;
  }

  /**
   * Validate current token with server using /me endpoint
   */
  async validateToken(): Promise<TokenValidationResponse | null> {
    const token = getAccessToken();
    if (!token) return null;

    try {
      // Decode the token to check if it's valid
      const decodedToken = this.getStoredUserInfo();
      if (decodedToken && decodedToken.sub) {
        // If we can decode the token, consider it valid
        return {
          valid: true,
          should_refresh: false,
          expires_in: 3600
        };
      }
      
      return null;
    } catch (err) {
      return null;
    }
  }

  /**
   * Make authenticated API request using JWT client
   */
  async authenticatedFetch(url: string, options: import('axios').AxiosRequestConfig = {}): Promise<any> {
    if (!this.jwtClient) {
      throw new Error('JWT client not initialized');
    }
    // This now uses axios via the interceptor, so we just call it.
    return axios(url, options);
  }

  /**
   * Set tokens and initialize JWT client
   */
  setTokens(accessToken: string, refreshToken: string): boolean {
    // This is no longer needed as the token refresh is handled by the axios interceptor
    return true;
  }

  initiateOAuthLogin(provider: OAuthProvider): void {
    // Get the current URL for redirect
    const redirectUri = `${window.location.origin}/auth/callback`;
    
    // Construct the OAuth login URL
    const loginUrl = `${this.baseUrlV1}/${provider}/login?redirect_uri=${encodeURIComponent(redirectUri)}&response_type=url&sign_up_origin=security_app`;
    
    log('Initiating OAuth login with URL:', loginUrl);
    
    // Redirect to the OAuth provider
    window.location.href = loginUrl;
  }

  handleOAuthCallback(): OAuthResponse | null {
    // Debug logging
    log('OAuth Callback Debug:');
    log('Full URL:', window.location.href);
    log('Search params:', window.location.search);
    log('Hash params:', window.location.hash);
    
    // Get tokens from URL parameters (query string)
    const urlParams = new URLSearchParams(window.location.search);
    let accessToken = urlParams.get('access_token');
    let refreshToken = urlParams.get('refresh_token');
    let code = urlParams.get('code');

    // Also check URL hash in case tokens are in fragment
    if (!accessToken && !refreshToken && window.location.hash) {
      log('Checking URL hash for tokens...');
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      accessToken = hashParams.get('access_token');
      refreshToken = hashParams.get('refresh_token');
      code = hashParams.get('code');
      
      log('Hash parameters:');
      hashParams.forEach((value, key) => {
        log(`${key}: ${value ? '[PRESENT]' : '[EMPTY]'}`);
      });
    }

    log('Access token found:', !!accessToken);
    log('Refresh token found:', !!refreshToken);
    log('Code found:', !!code);
    
    // Log all URL parameters for debugging
    log('Query parameters:');
    urlParams.forEach((value, key) => {
      log(`${key}: ${value ? '[PRESENT]' : '[EMPTY]'}`);
    });

    if (accessToken) {
      log('Access token found, storing tokens using JWT client');
      // Store tokens using the new setTokens method
      if (refreshToken) {
        this.setTokens(accessToken, refreshToken);
      }
      return { 
        access_token: accessToken, 
        refresh_token: refreshToken || undefined 
      };
    }

    if (code) {
      log('Authorization code found, returning code');
      return { code };
    }

    log('No tokens or code found in URL parameters');
    return null;
  }

  async getUserInfo(): Promise<User | null> {
    try {
      // Use the new fetchUser function from api.ts
      const { fetchUser } = await import('./api');
      return await fetchUser();
    } catch (err: any) {
      error('Error fetching user info:', err);
      if (err.response?.status === 401) {
        this.handleSessionExpired();
      }
      return null;
    }
  }

  logout(): void {
    // Clear JWT client tokens first
    if (this.jwtClient) {
      this.jwtClient.clearTokens();
    }
    
    // Clear OAuth tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    
    // Clear all other authentication-related data
    
    // Import encryption utility to remove encrypted user data
    import('../utils/encryption').then(({ setUserData }) => {
      // Set to null to effectively remove the data
      setUserData(null);
    });
    
    // Clear any walkthrough-related data that might persist user state
    localStorage.removeItem('walkthroughCompleted');
    localStorage.removeItem('walkthroughDismissed');
    localStorage.removeItem('walkthrough-resume');
    localStorage.removeItem('walkthrough-next-step');
    localStorage.removeItem('firstVisit');
    
    // Clear any section-specific walkthrough data
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('section-') && key.endsWith('-started')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear axios default headers
    delete (window as any).axios?.defaults?.headers?.common?.['Authorization'];
  }

  isAuthenticated(): boolean {
    if (this.jwtClient) {
      return this.jwtClient.isAuthenticated();
    }
    // Fallback to basic token check
    return true;
  }

  /**
   * Get JWT client instance
   */
  getJWTClient(): JWTRefreshClient | null {
    return this.jwtClient;
  }

  /**
   * Get user info from JWT client
   */
  getStoredUserInfo(): any {
    const token = getAccessToken();
    if (!token) return null;
    
    try {
      // Use the imported jwtDecode
      return jwtDecode(token);
    } catch (e) {
      // Fallback to JWT client if available
      if (this.jwtClient) {
        return this.jwtClient.getUserInfo();
      }
      error('Failed to decode token', e);
      return null;
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    try {
      const response = await axios.post(`${this.baseUrlV1}/verify-email?token=${encodeURIComponent(token)}`);
      return response.data;
    } catch (err : any) {
      error('Error verifying email:', err);
      throw err.response?.data || err;
    }
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<{ message: string }> {
    try {
      const callbackUrl = `https://app.security.openana.ai/verify-email`;
      const response = await axios.post(`${this.baseUrlV1}/resend-verification`, {
        email,
        callback_url: callbackUrl
      });
      return response.data;
    } catch (err : any) {
      error('Error resending verification:', err);
      throw err.response?.data || err;
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const callbackUrl = `https://app.security.openana.ai/reset-password`;
      const response = await axios.post(`${this.baseUrlV1}/password-reset`, {
        email,
        callback_url: callbackUrl
      });
      return response.data;
    } catch (err : any) {
      error('Error requesting password reset:', err);
      if (err.response?.data?.detail?.message) {
        const rateLimitError = new Error(err.response.data.detail.message);
        (rateLimitError as any).isRateLimit = true;
        (rateLimitError as any).rateLimitData = err.response.data.detail;
        throw rateLimitError;
      }
      throw err.response?.data || err;
    }
  }

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    try {
      const { encryptData } = await import('../services/api');
      
      const encryptedNew = encryptData(newPassword);
      const encryptedConfirm = encryptData(confirmPassword);

      const response = await axios.post(`${this.baseUrlV1}/password-reset/confirm`, {
        token,
        new_password: encryptedNew,
        confirm_password: encryptedConfirm,
      });

      return response.data;
    } catch (err: any) {
      error('Error confirming password reset:', err);
      throw err.response?.data || err;
    }
  }
  async silentRefresh(): Promise<string | null> {
    if (this.silentRefreshPromise) {
      return this.silentRefreshPromise;
    }

    this.silentRefreshPromise = new Promise(async (resolve) => {
      try {
        log('Attempting silent refresh...');
        const response = await axios.post<{ access_token: string }>(
          `${this.baseUrlV2}/refresh-token`,
          {},
          { withCredentials: true }
        );
        log('Silent refresh successful, new access token received.');
        resolve(response.data.access_token);
      } catch (err: any) {
        error('Silent refresh failed. This is expected if the user is not logged in.', err.response?.data || err.message);
        resolve(null);
      } finally {
        this.silentRefreshPromise = null;
      }
    });

    return this.silentRefreshPromise;
  }
}

export const authService = new AuthService();
